import {
  APIErrorStatus,
  ListingType,
  ModalName,
  PaymentGatewayName,
} from '@share/enums';
import { AxiosError } from 'axios';
import { BackendResponse } from '@share/interfaces';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { market } from '@state/market';
import { toast } from 'react-toastify';
import { toggleBootstrapModal } from '@util/helper';
import { useAPI, useCurrency, useHelper } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { web3 } from '@state/web3';
import Agreement from './Agreement';
import CheckoutBidBuyFrame from '@com/payment/CheckoutBidBuyFrame';
import DotLoading from '@com/utils/DotLoading';
import GkashPaymentBtn from '@com/buttons/GkashPaymentBtn';
import PaymentRow from '@com/utils/PaymentRow';

interface Params {
  tokenId: number;
  collectionAddress: string;
  contractChainId: number;
  orderId: number;
  currencyId: number;
  paymentGateway: PaymentGatewayName;
  price: string;
  loadLaunchpadData: () => void;
}

const BuyModal = forwardRef(
  (
    {
      tokenId,
      collectionAddress,
      contractChainId,
      orderId,
      currencyId,
      paymentGateway,
      price,
      loadLaunchpadData,
    }: Params,
    ref
  ) => {
    /* Define state / hook */
    const { getCurrencyNameById, isFiatCurrency } = useCurrency();
    const navigate = useNavigate();
    const { isSigned, makeBuy, payment, getBalance } = market.useContainer();
    const { getAddress } = web3.useContainer();
    const { t } = useTranslation();
    const { displayCurrency } = useHelper();
    const [isShow, setIsShow] = useState<boolean>();
    const [isChecked, setIsChecked] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [balance, setBalance] = useState<string>(String(0));
    const { getGasReport, processGkashBuy } = useAPI();
    const buyModal = useRef<HTMLDivElement>(null);

    const isFiat = isFiatCurrency(currencyId) || false;
    const { data } = useQuery(
      [getGasReport.name, orderId],
      () => getGasReport(orderId),
      {
        enabled: isFiat && isShow === true, // Prevent undefined also execute the query
        refetchInterval: 2 * 60 * 1000,
        retry: false,
        onError(error: AxiosError<BackendResponse<undefined>>) {
          if (error.response?.data?.code === APIErrorStatus.ORDER_NOT_ACTIVE) {
            loadLaunchpadData();
          }
          setTimeout(() => setIsShow(false), 500);
        },
      }
    );

    /* Custom Data Object for render or other usages */
    const currencyName = getCurrencyNameById(currencyId);

    const toggleModal = useDebouncedCallback(
      async () => {
        // If crypto
        if (!isFiatCurrency(currencyId)) {
          setBalance(await getBalance(currencyId));
        }

        if (!isLoading) setIsShow(!isShow);
      },
      400,
      { leading: true, trailing: true }
    );

    const handleCryptoBuy = useDebouncedCallback(
      async () => {
        const isLogin = await payment();
        if (!isLogin) return;

        setIsLoading(true);
        const isTxSuccess = await makeBuy(
          collectionAddress,
          tokenId,
          currencyId,
          Number(price)
        );
        setIsLoading(false);

        if (!isTxSuccess) return;
        toggleModal();
        navigate(`/profile/my`);
      },
      500,
      { leading: true, trailing: false }
    );

    const handleGkashBuy = async () => {
      if (!isChecked) {
        toast.info(t('toastMessage.checkTerms'));
        return null;
      }

      const isLoginDoubleCheck = await payment();
      if (!isLoginDoubleCheck) return null;
      if (!data) return null;

      return processGkashBuy({
        chainId: contractChainId,
        collectionAddress,
        tokenId,
        buyerAddress: getAddress(),
        totalPrice: data.total_price,
      });
    };

    /* Data Change Listeners - useEffect/useMemo/useCallback */
    useImperativeHandle(ref, () => ({ toggleModal }));

    useEffect(() => {
      if (isShow != null) toggleBootstrapModal(ModalName.BUY);
    }, [isShow]);

    /* Styling */

    /* Render */
    return (
      <div
        ref={buyModal}
        aria-hidden
        className="modal fade popup"
        id={ModalName.BUY}
        role="dialog"
      >
        <div
          className="modal-dialog modal-dialog-centered mt-0 mb-0"
          role="document"
        >
          <div className="modal-content">
            <button
              className="close"
              type="button"
              onClick={() => {
                toggleModal();
              }}
            >
              <span aria-hidden>&times;</span>
            </button>

            <div className="modal-body space-y-20 pd-40">
              <h3>{t('modal.completeCheckout')}</h3>
              <hr />
              {isLoading ? (
                <div>
                  <DotLoading />
                  <div className="row mt-4">
                    <div className="h1 col-12 text-center mt-5">
                      {isSigned
                        ? t('modal.waitContract')
                        : t('modal.signContract')}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  {isFiat && (
                    <>
                      <PaymentRow
                        currency={currencyName}
                        price={data?.base_price || price}
                        title={t('modal.subtotal')}
                      />
                      <PaymentRow
                        currency={currencyName}
                        price={data?.base_gas}
                        title={t('modal.transactionFee')}
                      />
                      {/* <PaymentRow
                      currency={currencyName}
                      price={data?.service?.payment_fee}
                      title={t('modal.handlingFee', {
                        percentage: showPercentage(
                          Number(data?.service?.payment_fee)
                        ),
                      })}
                    /> */}
                    </>
                  )}
                  <PaymentRow
                    isTotal
                    currency={currencyName}
                    price={data?.total_price || price} // fiat = total_price and crypto = price
                    title={t('modal.totalPaid')}
                  />

                  {isFiat && (
                    <Agreement
                      isChecked={isChecked}
                      setIsChecked={setIsChecked}
                    />
                  )}

                  {isFiat ? (
                    <h1>
                      {paymentGateway === PaymentGatewayName.CHECKOUT && (
                        <CheckoutBidBuyFrame
                          checked={isChecked}
                          collectionAddress={collectionAddress}
                          contractChainId={contractChainId}
                          tokenId={tokenId}
                          totalPrice={data?.total_price || Number(price)}
                          type={ListingType.BUY}
                        />
                      )}
                      {paymentGateway === PaymentGatewayName.GKASH && (
                        <GkashPaymentBtn prepayHook={handleGkashBuy} />
                      )}
                    </h1>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between mt-5 mb-5">
                        <p>{t('modal.balanceInWallet')}</p>
                        <div className="text-right mt-1">
                          <div className="price color-popup">
                            {displayCurrency(balance, currencyName)}
                          </div>
                        </div>
                      </div>
                      <button
                        aria-label={t('modal.buyInCryto')}
                        className="btn btn-primary"
                        disabled={isLoading}
                        type="button"
                        onClick={() => {
                          handleCryptoBuy();
                        }}
                      >
                        {t('modal.buyInCryto')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default BuyModal;
